import React, { } from 'react'
import { useTranslation } from 'react-i18next'
import { colors, css, fonts, frames, glyphs } from '../../../Styles'
import { currentLfViewTypes, useLfContext } from '../Modals/_Modals/lfPenContext';
import { Location, SectionsInfo } from '../../../Services';
import LF_PEN from './lfPen';

interface lfPenslist_overrides {
  primaryColor?: colors;
  textColor?: colors;
  fittedWidth?: boolean;
  border?: string;
  attention?: number
}

interface lf_penlist_Props{
  focus_view?: boolean;
  section?: SectionsInfo
  popoverContent?: (index: number, pen: Location) => JSX.Element;
  overrides?: lfPenslist_overrides | ((index: number) => lfPenslist_overrides);
}
/**
 * SECTION_BASIC
 * @param sectionId Unique identifier for each section.
 * @param sectionType Basic, advanced and single. Each renders different content of animal data.
 * @param onPenClick OnClick event to handle penId.
 * @param onAddBtn OnClick event to handle Add btn.
 * @param onMoveBtn OnClick event to handle Move btn.
 * @param onEditBtn OnClick event to handle Edit btn.
 * @returns a section of pens for Freeda Location Feeder systems.
 */
export default function LF_PENLIST({ focus_view, section, popoverContent, overrides }: lf_penlist_Props) {
  const { t } = useTranslation()
  const { state: { currentPenViewType } } = useLfContext();
  
  const _framed = currentPenViewType !== currentLfViewTypes.systemControle

  const pens = section?.section_locations ?? []
  
  if (!pens || pens.length === 0) return null;

  // const maxRowsAndCols = pens.reduce(
  //   (acc, pen) => {
  //     const [endRow, endCol] = pen.visual ? pen.visual.split('/').map(Number) : [0, 0];
  //     return {
  //       rows: Math.max(acc.rows, endRow),
  //       cols: Math.max(acc.cols, endCol),
  //     };
  //   },
  //   { rows: 0, cols: 0 }
  // );

  const maxRowsAndCols = pens.reduce(
    (acc, pen) => {
      if (!pen.visual) return acc;
  
      let rows = 0;
      let cols = 0;
  
      if (pen.visual.includes("-")) {
        const afterDash = pen.visual.split("-")[1];
        [rows, cols] = afterDash.split("/").map(Number);
      } else {
        const [endRow, endCol] = pen.visual.split('/').map(Number);
      return {
        rows: Math.max(acc.rows, endRow),
        cols: Math.max(acc.cols, endCol),
      };
      }
  
      return {
        rows: Math.max(acc.rows, rows),
        cols: Math.max(acc.cols, cols),
      };
    },
    { rows: 0, cols: 0 }
  );

  const _gridTemplateColumns = `repeat(${maxRowsAndCols.cols}, 1fr)`
  const _gridTemplateRows = `repeat(${maxRowsAndCols.rows}, 1fr)`

  if (pens.length === 0) {
    return <p style={{ ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyDarkPrimary, opacity: "0.3", fontStyle: "italic" }}>{t("noData", { defaultValue: "no data" })}</p>;
  }

  const renderContent = () => {
    return pens.map((pen, index) => {

      const _overrides = typeof overrides === "function" ? overrides(index) : overrides ?? {};
      const _popoverContent = typeof popoverContent === "function" ? popoverContent(index, pen) : popoverContent ?? undefined;
      const _gridArea = pen.visual ? pen.visual.replace(/^"|"$/g, '').replace(/^'|'$/g, '').split("-")[0] : ""

      return (
        <LF_PEN
          focus_view={focus_view}
          key={pen.location_id}
          pen={pen}
          popoverContent={_popoverContent}
          gridArea={_gridArea}
          overrides={{
            primaryColor: _overrides.primaryColor,
            textColor: _overrides.textColor,
            width: _overrides.fittedWidth && maxRowsAndCols.cols === 4 ? "56px" : undefined,
            border: _overrides.border,
            attention: _overrides.attention
          }}
        />
      )
    });
  };
  
  return (
    <frames.SectionFrame key={section?.section_id} $isHovered={_framed} style={{padding: _framed ? "10px 12px 14px 12px" : "0px"}}>
      {_framed ? <p style={{...fonts.header, ...fonts.fontSize3, color: colors.greyDarkPrimary}}>{section?.name}</p> : undefined}
      <div style={{ display: "grid", gridTemplateColumns: _gridTemplateColumns, gridTemplateRows: _gridTemplateRows, width: "fit-content", height: "fit-content", gap: "10px", borderRadius: "10px", overflow: "hidden" }}>
        {renderContent()}
      </div>
      {_framed ? <>
        <div style={{ position: "absolute", top: 0, right: 0 }}><glyphs.Corner fillColor={colors.greyTertiary} rotate={90} /></div>
        <div style={{position: "absolute", bottom: 0, right: 0}}><glyphs.Corner fillColor={colors.greyTertiary} rotate={180}/></div>
        <div style={{position: "absolute", bottom: 0, left: 0}}><glyphs.Corner fillColor={colors.greyTertiary} rotate={270}/></div>
        <div style={{ position: "absolute", top: 0, left: 0 }}><glyphs.Corner fillColor={colors.greyTertiary} /></div>
      </> : <></>}
    </frames.SectionFrame>
  );
}